// /login/callback — landing page after the OAuth callback redirects
// back from /api/auth/google/callback.
//
// The backend set the refresh-token cookie. This page calls
// AuthContext.refresh() to consume that cookie via /api/auth/refresh,
// which produces an in-memory access token + signed-in session.
//
// Status param values written by the backend:
//   status=ok                          — sign-in succeeded; route to /quotes
//   status=ok & new_user=1             — first-time Google user
//   status=ok & verify_pending=1       — show a verify-email reminder
//                                        before routing on (tier pricing
//                                        gated until verification)
//   status=error & reason=<message>    — render the error + link back

import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext.js';
import { ANALYTICS_EVENTS, trackAuthEvent } from '../lib/analytics/index.js';

export function LoginCallback(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const status = params.get('status');
  const reason = params.get('reason');
  const newUser = params.get('new_user') === '1';
  const verifyPending = params.get('verify_pending') === '1';

  const [stage, setStage] = useState<
    'verifying' | 'ok' | 'verify_reminder' | 'error'
  >(status === 'error' ? 'error' : 'verifying');

  useEffect(() => {
    if (status === 'error') return;
    void (async () => {
      // The RESOLVED user, not the context — this runs in the same tick as the
      // refresh, so the context still holds the pre-refresh value and the
      // test-account gate would read the wrong identity.
      const signedIn = await refresh();
      // Google's half of the pair Login.tsx emits for the password path. A
      // first-time Google arrival is BOTH a registration and a sign-in, so it
      // emits both rather than picking one — the funnel would otherwise
      // undercount Google registrations, which never touch Register.tsx.
      if (newUser) {
        trackAuthEvent(ANALYTICS_EVENTS.register_succeeded, signedIn?.email, { method: 'google' });
      }
      trackAuthEvent(ANALYTICS_EVENTS.login_succeeded, signedIn?.email, { method: 'google' });
      if (verifyPending) {
        setStage('verify_reminder');
      } else {
        setStage('ok');
        // Brief delay so the user reads "signed in" before redirect.
        setTimeout(() => navigate('/quotes', { replace: true }), 250);
      }
    })();
  }, [status, refresh, verifyPending, navigate, newUser]);

  if (stage === 'verifying') {
    return (
      <div className="card">
        <p>Finishing sign-in…</p>
      </div>
    );
  }

  if (stage === 'error') {
    return (
      <div className="card">
        <h2>Google sign-in didn&apos;t complete</h2>
        <div className="banner banner--error">
          <strong>{decodeErrorReason(reason)}</strong>
        </div>
        <p>
          You can <Link to="/login">try again</Link>, or use email and password.
        </p>
      </div>
    );
  }

  if (stage === 'verify_reminder') {
    return (
      <div className="card">
        <h2>{newUser ? 'Welcome to the Argo Print Portal' : 'Welcome back'}</h2>
        <div className="banner banner--warn">
          <strong>One more step to see your account pricing.</strong>
          <p style={{ margin: '8px 0 0' }}>
            We&apos;ve sent a verification link to your email. Until you click it, you&apos;ll
            see standard pricing only. The rest of the portal works as usual.
          </p>
        </div>
        <p>
          <button
            type="button"
            className="primary"
            onClick={() => navigate('/quotes', { replace: true })}
          >
            Continue to your quotes
          </button>
        </p>
      </div>
    );
  }

  return (
    <div className="card">
      <p>Signed in. Redirecting…</p>
    </div>
  );
}

function decodeErrorReason(reason: string | null): string {
  if (!reason) return 'Google sign-in failed. Please try again.';
  if (reason === 'invalid_state') {
    return 'Sign-in session expired before Google returned. Please try again.';
  }
  if (reason === 'access_denied') {
    return 'You declined the Google sign-in. No account was created.';
  }
  // Raw Google error codes are snake_case identifiers, not copy. The backend
  // only forwards sentence-form AppError messages, which read as-is.
  if (/^[a-z0-9_]+$/.test(reason)) return 'Google sign-in failed. Please try again.';
  return reason;
}
